import React, { useState } from "react";
import api from "../lib/api";
import { formatIQD, formatDate, todayISO } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import StatusBadge from "../components/StatusBadge";
import { Download } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { toast } from "sonner";

function DailyReport() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);

  const run = () => api.get("/reports/daily", { params: { report_date: date } }).then((r) => setData(r.data));
  const exportXls = () => {
    if (!data) return;
    const rows = data.rows.map((r) => ({
      "Order #": r.order_number,
      Employee: r.employee_name,
      Restaurant: r.restaurant_name,
      Meal: r.meal_description,
      Amount: r.total_amount,
      Paid: r.amount_paid,
      Outstanding: r.outstanding,
      Status: r.payment_status,
    }));
    exportToExcel(rows, "Daily Report", `daily-report-${date}`);
    toast.success("Excel exported");
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="daily-report-date" /></div>
          <Button onClick={run} data-testid="btn-run-daily-report">Run Report</Button>
          {data && <Button variant="outline" onClick={exportXls} data-testid="btn-export-daily"><Download size={14} className="mr-1" />Export Excel</Button>}
        </div>
      </Card>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Employees</div><div className="heading text-xl font-bold">{data.totals.total_employees}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Total Cost</div><div className="heading text-xl font-bold">{formatIQD(data.totals.total_cost)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Paid</div><div className="heading text-xl font-bold text-emerald-700">{formatIQD(data.totals.total_paid)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Outstanding</div><div className="heading text-xl font-bold text-rose-700">{formatIQD(data.totals.total_outstanding)}</div></Card>
          </div>
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-3">Order #</th><th className="p-3">Employee</th><th className="p-3">Meal</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Paid</th><th className="p-3 text-right">Outstanding</th><th className="p-3">Status</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t table-row-hover">
                    <td className="p-3 mono text-xs">{r.order_number}</td>
                    <td className="p-3 font-medium">{r.employee_name}</td>
                    <td className="p-3">{r.meal_description || "—"}</td>
                    <td className="p-3 text-right mono">{formatIQD(r.total_amount)}</td>
                    <td className="p-3 text-right mono text-emerald-700">{formatIQD(r.amount_paid)}</td>
                    <td className="p-3 text-right mono text-rose-700">{formatIQD(r.outstanding)}</td>
                    <td className="p-3"><StatusBadge status={r.payment_status} /></td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No orders on this date.</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function MonthlyReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const run = () => api.get("/reports/monthly", { params: { year, month } }).then((r) => setData(r.data));
  const exportXls = () => {
    if (!data) return;
    const rows = data.rows.map((r) => ({
      Employee: r.employee_name, Orders: r.orders, "Food Cost": r.food_cost, Payments: r.payments, Outstanding: r.outstanding,
    }));
    exportToExcel(rows, "Monthly Report", `monthly-report-${year}-${String(month).padStart(2, "0")}`);
    toast.success("Excel exported");
  };
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label>Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="mt-1.5 w-40" data-testid="monthly-month"><SelectValue /></SelectTrigger>
              <SelectContent>{monthNames.map((n, i) => <SelectItem key={n} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Year</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1.5 w-32" data-testid="monthly-year" /></div>
          <Button onClick={run} data-testid="btn-run-monthly">Run Report</Button>
          {data && <Button variant="outline" onClick={exportXls} data-testid="btn-export-monthly"><Download size={14} className="mr-1" />Export Excel</Button>}
        </div>
      </Card>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Orders</div><div className="heading text-xl font-bold">{data.totals.total_orders}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Employees</div><div className="heading text-xl font-bold">{data.totals.employees_count}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Food Cost</div><div className="heading text-xl font-bold">{formatIQD(data.totals.total_cost)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Payments</div><div className="heading text-xl font-bold text-emerald-700">{formatIQD(data.totals.total_payments)}</div></Card>
            <Card className="p-4"><div className="text-xs uppercase text-muted-foreground">Outstanding</div><div className="heading text-xl font-bold text-rose-700">{formatIQD(data.totals.total_outstanding)}</div></Card>
          </div>
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-3">Employee</th><th className="p-3 text-right">Orders</th><th className="p-3 text-right">Food Cost</th><th className="p-3 text-right">Payments</th><th className="p-3 text-right">Outstanding</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.employee_id} className="border-t table-row-hover">
                    <td className="p-3 font-medium">{r.employee_name}</td>
                    <td className="p-3 text-right mono">{r.orders}</td>
                    <td className="p-3 text-right mono">{formatIQD(r.food_cost)}</td>
                    <td className="p-3 text-right mono text-emerald-700">{formatIQD(r.payments)}</td>
                    <td className={`p-3 text-right mono font-semibold ${r.outstanding > 0 ? "text-rose-700" : "text-slate-500"}`}>{formatIQD(r.outstanding)}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No activity this month.</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

export default function Reports() {
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="heading text-2xl font-extrabold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground">Generate daily and monthly reports. Export to Excel with one click.</p>
      </div>
      <Tabs defaultValue="daily">
        <TabsList><TabsTrigger value="daily" data-testid="tab-daily-rep">Daily</TabsTrigger><TabsTrigger value="monthly" data-testid="tab-monthly-rep">Monthly</TabsTrigger></TabsList>
        <TabsContent value="daily" className="mt-4"><DailyReport /></TabsContent>
        <TabsContent value="monthly" className="mt-4"><MonthlyReport /></TabsContent>
      </Tabs>
    </div>
  );
}
